import React from "react";
import type { CustomNote } from "../components/CreateNoteModal/AITemplateModal/types";
import { DEFAULT_NOTE_TYPES, GWUL_NOTE_TYPES, LLT_NOTE_TYPES } from "../components/CreateNoteModal/constants";
import { isFromGWUL, isFromLLT } from "../domain/groups.domain";
import { NoteType } from "../domain/notes.domain";
import { PRACTICE_AREA } from "../domain/user.domain";
import { getCustomNoteList } from "../service/notes.service";
import { getTemplates } from "../service/templates.service";
import { setAllTemplates } from "../slices/appStateSlice";
import { useAppDispatch, useAppSelector } from "./redux-hooks";

const SOAP_NOTE_SLUG = "soap-note";

export type NudgeNoteTypeEntry = { name: string; type: string };

export type TemplateGroupKey = "mine" | "nudge" | "gallery";

/** One option in the template picker. ``value`` is what goes into ``recordingDetail.note_type``. */
export type TemplateOption = {
  label: React.ReactNode;
  /** A custom note's type id or a Nudge NoteType (numbers), or a gallery template's slug. */
  value: number | string;
  /** Plain name: Fuse can't search a JSX label, and the Start card prints it. */
  searchText: string;
  group: TemplateGroupKey;
  /** Gallery templates only: the template id the backend copies into "My Templates" on start. */
  id?: number;
  is_template?: boolean;
  content?: string;
  description?: string;
  tags?: unknown;
};

export type TemplateOptionGroup = {
  key: TemplateGroupKey;
  label: string;
  title: string;
  options: TemplateOption[];
};

// The sidebar mounts the Start card twice (desktop column and the mobile tab bar), so mounts
// that ask at the same moment share one request; any later mount asks afresh.
let customNotesInFlight: Promise<CustomNote[]> | null = null;
const fetchCustomNotes = (): Promise<CustomNote[]> => {
  if (!customNotesInFlight) {
    customNotesInFlight = getCustomNoteList()
      .then((response) => (response.data as CustomNote[]).filter((item) => item.status === 0))
      .finally(() => {
        customNotesInFlight = null;
      });
  }
  return customNotesInFlight;
};

/** The Nudge prebuilt types a user's group offers (the drawer only shows them to behavioral health). */
export const nudgeNoteTypesFor = (userInfo: Parameters<typeof isFromGWUL>[0]): NudgeNoteTypeEntry[] => {
  if (isFromGWUL(userInfo)) return GWUL_NOTE_TYPES;
  if (isFromLLT(userInfo)) return LLT_NOTE_TYPES;
  return DEFAULT_NOTE_TYPES;
};

/**
 * Everything the template picker offers, grouped the way the drawer shows it: My Templates
 * (own plus admin-shared), Nudge Templates (behavioral health only), Template Gallery.
 * Shared by the drawer's NoteTypeSelector and the one-click Start card so both list the
 * same templates under the same names.
 *
 * ``refreshKey`` refetches the user's own list when it changes (the drawer passes the
 * "template added from the drawer" flag). ``galleryOnMount`` fetches the gallery on every
 * mount, as the drawer always has; other callers get it from the store, or call
 * ``loadGallery`` when they are about to show it.
 */
export const useTemplateOptions = ({
  noteTypes,
  refreshKey,
  galleryOnMount = true,
}: {
  noteTypes: NudgeNoteTypeEntry[];
  refreshKey?: unknown;
  galleryOnMount?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const templates = useAppSelector((state) => state.appState.templates);
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const [customNoteList, setCustomNoteList] = React.useState<CustomNote[]>([]);
  const [customNotesLoaded, setCustomNotesLoaded] = React.useState(false);

  const loadGallery = React.useCallback(() => {
    getTemplates()
      .then((response) => {
        const sortedTemplates = [...response].sort((a, b) => {
          // Put soap-note template first
          if (a.slug === SOAP_NOTE_SLUG) return -1;
          if (b.slug === SOAP_NOTE_SLUG) return 1;
          // Sort the rest alphabetically by name
          return a.name.localeCompare(b.name);
        });
        dispatch(setAllTemplates(sortedTemplates));
      })
      .catch((error) => {
        console.error("Error fetching templates:", error);
      });
  }, [dispatch]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch only when the caller's key changes
  React.useEffect(() => {
    let cancelled = false;
    fetchCustomNotes()
      .then((data) => {
        if (!cancelled) setCustomNoteList(data);
      })
      .catch((error) => console.error("Error fetching custom note list:", error))
      .finally(() => {
        if (!cancelled) setCustomNotesLoaded(true);
      });
    if (galleryOnMount) loadGallery();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const groups = React.useMemo<TemplateOptionGroup[]>(
    () => [
      ...(customNoteList.length > 0
        ? [
            {
              key: "mine" as const,
              label: "My Templates",
              title: "My Templates",
              options: customNoteList.map((note): TemplateOption => {
                if (note.is_group_accessible) {
                  return {
                    label: (
                      <>
                        {note.name}
                        <span style={{ fontStyle: "italic", fontSize: "0.9em", marginLeft: "4px" }}>
                          - shared by group admin
                        </span>
                      </>
                    ),
                    value: note.id,
                    // Fuse can't search a JSX label — searchText carries the plain name so
                    // admin-shared templates show up in the dropdown search like everything else.
                    searchText: note.name,
                    group: "mine",
                  };
                }
                return {
                  label: note.name,
                  value: note.id,
                  searchText: note.name,
                  group: "mine",
                };
              }),
            },
          ]
        : []),
      // Only show Nudge Templates for Behavioral Health users
      ...(userInfo?.practice_area === PRACTICE_AREA.BEHAVIORAL_HEALTH
        ? [
            {
              key: "nudge" as const,
              label: "Nudge Templates",
              title: "Nudge Templates",
              options: noteTypes.map(
                (note): TemplateOption => ({
                  label: note.name,
                  value: Number.parseInt(String(NoteType[note.type as keyof typeof NoteType])),
                  searchText: note.name,
                  group: "nudge",
                }),
              ),
            },
          ]
        : []),
      ...(templates.length > 0
        ? [
            {
              key: "gallery" as const,
              label: "Template Gallery",
              title: "Template Gallery",
              options: templates.map((note) => ({
                label: note.name,
                value: note.slug, // Already a string, ensure consistency
                id: note.id,
                is_template: true,
                content: note.content,
                description: note.description,
                tags: note.tags,
                searchText: note.name,
                group: "gallery" as const,
              })),
            },
          ]
        : []),
    ],
    [customNoteList, noteTypes, templates, userInfo?.practice_area],
  );

  const options = React.useMemo(() => groups.flatMap((group) => group.options), [groups]);

  return { groups, options, customNotesLoaded, loadGallery };
};
